import React, { useState, useEffect, useMemo } from 'react';
import { getCurrentMediumsStatus, getMediumDomain, isMediumVip } from '../data/mediums';
import MediumCard from './MediumCard';
import { Medium, MediumDomain } from '../types';
import { getFavoriteMediumIds } from './favorites';
import { 
    HeartIcon, 
    BriefcaseIcon,
    TrendingUpIcon,
    SparklesIcon, 
    StarIcon, 
    SearchIcon,
    CrownIcon,
    XIcon
} from './Icons';

interface MediumsListProps {
    onChatStart: (medium: Medium) => void;
    onProfileClick: (medium: Medium) => void;
    initialCategory?: string | null;
}

export const MediumsList: React.FC<MediumsListProps> = ({ 
    onChatStart, 
    onProfileClick, 
    initialCategory 
}) => {
  const [activeCategory, setActiveCategory] = useState<string>(initialCategory || 'all');
  const [searchTerm, setSearchTerm] = useState('');
  const [favoriteMediumIds, setFavoriteMediumIds] = useState<string[]>(getFavoriteMediumIds());

  useEffect(() => {
      if (initialCategory) {
          setActiveCategory(initialCategory);
      }
  }, [initialCategory]);

  useEffect(() => {
      const handleFavs = () => {
          setFavoriteMediumIds(getFavoriteMediumIds());
      };
      window.addEventListener('astro_favorites_updated', handleFavs);
      return () => window.removeEventListener('astro_favorites_updated', handleFavs);
  }, []);

  const allMediums = useMemo(() => {
      const currentStatusMediums = getCurrentMediumsStatus();
      const active = currentStatusMediums.filter(m => m.status === 'available' || m.status === 'busy');
      const inactive = currentStatusMediums.filter(m => m.status === 'away');
      return [...active, ...inactive];
  }, []);

  // Groupements par domaine
  const amourMediums = useMemo(() => {
      return allMediums.filter(m => (m.domain || getMediumDomain(m)) === 'Amour');
  }, [allMediums]);

  const travailMediums = useMemo(() => {
      return allMediums.filter(m => (m.domain || getMediumDomain(m)) === 'Travail');
  }, [allMediums]);

  const financeMediums = useMemo(() => {
      return allMediums.filter(m => (m.domain || getMediumDomain(m)) === 'Finance');
  }, [allMediums]);

  const vipMediums = useMemo(() => {
      return allMediums.filter(m => isMediumVip(m));
  }, [allMediums]);

  // Catégories avec leurs couleurs vibrantes et attirantes (100% SVG)
  const themeCategories = [
      { 
          id: 'all', 
          label: 'Tous les Domaines', 
          icon: <SparklesIcon className="w-4 h-4 text-amber-300" />,
          desc: 'Groupés par spécialité',
          activeClass: 'bg-gradient-to-br from-purple-700 via-indigo-700 to-purple-800 text-white border-amber-400/80 shadow-[0_0_20px_rgba(147,51,234,0.5)]',
          badgeText: 'Tous'
      },
      { 
          id: 'Amour', 
          label: 'Amour & Sentiments', 
          icon: <HeartIcon className="w-4 h-4 text-pink-400" />,
          desc: 'Couple, retour affectif',
          activeClass: 'bg-gradient-to-br from-pink-600 via-rose-600 to-purple-700 text-white border-pink-400 shadow-[0_0_22px_rgba(244,63,94,0.55)]',
          badgeText: 'Rose Passion'
      },
      { 
          id: 'Travail', 
          label: 'Travail & Carrière', 
          icon: <BriefcaseIcon className="w-4 h-4 text-cyan-300" />,
          desc: 'Évolution pro & projets',
          activeClass: 'bg-gradient-to-br from-cyan-600 via-blue-600 to-indigo-700 text-white border-cyan-400 shadow-[0_0_22px_rgba(6,182,212,0.55)]',
          badgeText: 'Cyan Pro'
      },
      { 
          id: 'Finance', 
          label: 'Argent & Finances', 
          icon: <TrendingUpIcon className="w-4 h-4 text-amber-300" />,
          desc: 'Gains, matériel & chance',
          activeClass: 'bg-gradient-to-br from-amber-500 via-yellow-500 to-emerald-600 text-black font-extrabold border-amber-300 shadow-[0_0_22px_rgba(245,158,11,0.55)]',
          badgeText: 'Or & Abondance'
      },
      { 
          id: 'vip', 
          label: 'Salon VIP (+1000)', 
          icon: <CrownIcon className="w-4 h-4 text-amber-300 fill-amber-300" />,
          desc: "Réservé membres d'élite",
          activeClass: 'bg-gradient-to-r from-amber-400 via-yellow-200 to-amber-500 text-black font-black border-amber-200 shadow-[0_0_25px_rgba(251,191,36,0.7)]',
          badgeText: 'VIP Privilège'
      },
      { 
          id: 'available', 
          label: 'En Ligne Direct', 
          icon: <span className="w-2.5 h-2.5 rounded-full bg-emerald-400 animate-pulse inline-block" />,
          desc: 'Disponibles maintenant',
          activeClass: 'bg-gradient-to-br from-emerald-600 to-teal-700 text-white border-emerald-400 shadow-[0_0_20px_rgba(16,185,129,0.5)]',
          badgeText: 'En Ligne'
      },
      { 
          id: 'favorites', 
          label: 'Mes Favoris', 
          icon: <StarIcon className="w-4 h-4 text-amber-400 fill-amber-400" />,
          desc: 'Vos voyants coup de cœur',
          activeClass: 'bg-gradient-to-br from-amber-600 to-rose-700 text-white border-amber-400 shadow-[0_0_20px_rgba(245,158,11,0.5)]',
          badgeText: 'Favoris'
      }
  ];

  // Filtrage pour la vue ciblée ou recherche
  const filteredMediums = useMemo(() => {
    return allMediums.filter(m => {
      const domain = m.domain || getMediumDomain(m);
      const text = (m.name + ' ' + m.specialties.join(' ') + ' ' + m.tags.join(' ') + ' ' + (m.description || '')).toLowerCase();

      // 1. Filtrage par thème / catégorie
      if (activeCategory === 'available' && m.status !== 'available') return false;
      if (activeCategory === 'favorites' && !favoriteMediumIds.includes(m.id.toString()) && !favoriteMediumIds.includes(String(m.id))) return false;
      if (activeCategory === 'vip' && !isMediumVip(m)) return false;
      if (activeCategory === 'Amour' && domain !== 'Amour') return false;
      if (activeCategory === 'Travail' && domain !== 'Travail') return false;
      if (activeCategory === 'Finance' && domain !== 'Finance') return false;

      // 2. Recherche textuelle
      if (searchTerm.trim()) {
          const clean = searchTerm.toLowerCase().trim();
          const matchSearch = text.includes(clean);
          if (!matchSearch) return false;
      }

      return true;
    });
  }, [allMediums, activeCategory, searchTerm, favoriteMediumIds]);

  const isGroupedView = activeCategory === 'all' && !searchTerm.trim();

  return (
    <div className="min-h-screen bg-[#08081A] text-white pt-6 pb-28 px-4 max-w-6xl mx-auto animate-fade-in">
      
      {/* Header Section */}
      <div className="text-center mb-6">
        <span className="text-xs font-bold uppercase tracking-widest text-amber-300 block mb-1">
          Cabinet de Voyance Privée
        </span>
        <h1 className="text-3xl sm:text-4xl lg:text-5xl font-serif font-bold text-white tracking-tight">
          Nos Médiums par Domaine
        </h1>
        <p className="text-xs sm:text-sm text-purple-200/80 max-w-2xl mx-auto mt-2 leading-relaxed">
          Explorez nos praticiens regroupés selon votre préoccupation : <span className="text-pink-300 font-bold">Amour & Relations</span>, <span className="text-cyan-300 font-bold">Travail & Carrière</span> ou <span className="text-amber-300 font-bold">Argent & Finances</span>.
        </p>
      </div>

      {/* BOUTONS DE FILTRAGE PAR DOMAINE ET VIP */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-7 gap-2.5 mb-6 max-w-6xl mx-auto">
        {themeCategories.map(cat => {
          const isActive = activeCategory === cat.id;
          return (
            <button
              key={cat.id}
              onClick={() => setActiveCategory(cat.id)}
              className={`p-3 rounded-2xl flex flex-col items-center justify-center text-center transition-all duration-200 cursor-pointer border ${
                isActive
                  ? `${cat.activeClass} scale-105`
                  : 'bg-[#12102A]/90 hover:bg-[#1A1033] text-purple-200/90 hover:text-white border-purple-900/40 hover:border-purple-500/50'
              }`}
            >
              <div className="mb-1.5 flex items-center justify-center">
                {cat.icon}
              </div>
              <span className={`text-xs font-bold leading-tight ${isActive ? 'text-inherit font-black' : 'text-white'}`}>
                {cat.label}
              </span>
              <span className={`text-[10px] hidden sm:block mt-0.5 truncate max-w-full font-light ${isActive ? 'opacity-90' : 'text-purple-300/70'}`}>
                {cat.desc}
              </span>
            </button>
          );
        })}
      </div>

      {/* Barre de Recherche */}
      <div className="max-w-xl mx-auto mb-8">
        <div className="relative">
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Rechercher un médium par prénom, spécialité, don..."
            className="w-full bg-[#12102A] border border-purple-900/60 focus:border-gold-500/80 rounded-2xl py-3 pl-11 pr-10 text-xs sm:text-sm text-white placeholder-purple-300/40 focus:outline-none transition-all shadow-inner"
          />
          <span className="absolute left-4 top-3.5 text-purple-400">
            <SearchIcon className="w-4 h-4" />
          </span>
          {searchTerm && (
            <button
              onClick={() => setSearchTerm('')}
              className="absolute right-3.5 top-3 text-xs bg-purple-900/60 hover:bg-purple-800 text-purple-200 p-1 rounded-lg"
            >
              <XIcon className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
      </div>

      {/* AFFICHAGE GROUPÉ PAR DOMAINE QUAND SUR 'TOUS LES DOMAINES' */}
      {isGroupedView ? (
        <div className="space-y-12">
          
          {/* ================= SECTION 1: AMOUR & SENTIMENTS ================= */}
          <section className="space-y-5">
            <div className="bg-gradient-to-r from-pink-950/80 via-purple-950/60 to-[#120822] border border-pink-500/40 rounded-3xl p-5 sm:p-6 shadow-[0_0_30px_rgba(244,63,94,0.18)] flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <div className="flex items-center gap-3.5">
                <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-pink-500 to-rose-600 flex items-center justify-center shadow-[0_0_15px_rgba(244,63,94,0.4)] shrink-0">
                  <HeartIcon className="w-6 h-6 text-white fill-white/40" />
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <h2 className="text-xl sm:text-2xl font-serif font-bold text-white tracking-tight">
                      Amour & Sentiments
                    </h2>
                    <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-pink-500/20 text-pink-300 border border-pink-500/40">
                      {amourMediums.length} Experts
                    </span>
                  </div>
                  <p className="text-xs sm:text-sm text-pink-200/80 mt-0.5">
                    Retour affectif, fidélité, âme sœur, déblocage relationnel & alchimie amoureuse.
                  </p>
                </div>
              </div>

              <button
                onClick={() => setActiveCategory('Amour')}
                className="text-xs font-bold bg-pink-900/40 hover:bg-pink-800/60 text-pink-200 hover:text-white px-3.5 py-2 rounded-xl border border-pink-500/30 transition-all cursor-pointer shrink-0"
              >
                Filtrer uniquement Amour →
              </button>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-5">
              {amourMediums.slice(0, 8).map(medium => (
                <MediumCard
                  key={medium.id}
                  medium={medium}
                  onChatStart={onChatStart}
                  onProfileClick={onProfileClick}
                />
              ))}
            </div>

            {amourMediums.length > 8 && (
              <div className="text-center pt-2">
                <button
                  onClick={() => setActiveCategory('Amour')}
                  className="text-xs font-bold text-pink-300 hover:text-pink-100 bg-pink-950/40 hover:bg-pink-900/60 border border-pink-500/30 px-5 py-2.5 rounded-2xl transition-all cursor-pointer"
                >
                  Voir tous les {amourMediums.length} experts en Amour & Sentiments →
                </button>
              </div>
            )}
          </section>


          {/* ================= SECTION 2: TRAVAIL & CARRIÈRE ================= */}
          <section className="space-y-5">
            <div className="bg-gradient-to-r from-cyan-950/80 via-blue-950/60 to-[#081326] border border-cyan-500/40 rounded-3xl p-5 sm:p-6 shadow-[0_0_30px_rgba(6,182,212,0.18)] flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <div className="flex items-center gap-3.5">
                <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center shadow-[0_0_15px_rgba(6,182,212,0.4)] shrink-0">
                  <BriefcaseIcon className="w-6 h-6 text-white" />
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <h2 className="text-xl sm:text-2xl font-serif font-bold text-white tracking-tight">
                      Travail & Carrière
                    </h2>
                    <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-cyan-500/20 text-cyan-300 border border-cyan-500/40">
                      {travailMediums.length} Experts
                    </span>
                  </div>
                  <p className="text-xs sm:text-sm text-cyan-200/80 mt-0.5">
                    Évolution professionnelle, reconversion, signature de contrats & réussite de vos projets.
                  </p>
                </div>
              </div>

              <button
                onClick={() => setActiveCategory('Travail')}
                className="text-xs font-bold bg-cyan-900/40 hover:bg-cyan-800/60 text-cyan-200 hover:text-white px-3.5 py-2 rounded-xl border border-cyan-500/30 transition-all cursor-pointer shrink-0"
              >
                Filtrer uniquement Travail →
              </button>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-5">
              {travailMediums.slice(0, 8).map(medium => (
                <MediumCard
                  key={medium.id}
                  medium={medium}
                  onChatStart={onChatStart}
                  onProfileClick={onProfileClick}
                />
              ))}
            </div>

            {travailMediums.length > 8 && (
              <div className="text-center pt-2">
                <button
                  onClick={() => setActiveCategory('Travail')}
                  className="text-xs font-bold text-cyan-300 hover:text-cyan-100 bg-cyan-950/40 hover:bg-cyan-900/60 border border-cyan-500/30 px-5 py-2.5 rounded-2xl transition-all cursor-pointer"
                >
                  Voir tous les {travailMediums.length} experts en Travail & Carrière →
                </button>
              </div>
            )}
          </section>


          {/* ================= SECTION 3: ARGENT & FINANCES ================= */}
          <section className="space-y-5">
            <div className="bg-gradient-to-r from-amber-950/80 via-yellow-950/60 to-[#1d1607] border border-amber-500/40 rounded-3xl p-5 sm:p-6 shadow-[0_0_30px_rgba(245,158,11,0.18)] flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <div className="flex items-center gap-3.5">
                <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-amber-400 to-yellow-600 flex items-center justify-center shadow-[0_0_15px_rgba(245,158,11,0.4)] shrink-0">
                  <TrendingUpIcon className="w-6 h-6 text-black" />
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <h2 className="text-xl sm:text-2xl font-serif font-bold text-white tracking-tight">
                      Argent & Finances
                    </h2>
                    <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-amber-500/20 text-amber-300 border border-amber-500/40">
                      {financeMediums.length} Experts
                    </span>
                  </div>
                  <p className="text-xs sm:text-sm text-amber-200/80 mt-0.5">
                    Déblocage financier, patrimoine, investissements, jeux, héritages & opportunités matérielles.
                  </p>
                </div>
              </div>

              <button
                onClick={() => setActiveCategory('Finance')}
                className="text-xs font-bold bg-amber-900/40 hover:bg-amber-800/60 text-amber-200 hover:text-white px-3.5 py-2 rounded-xl border border-amber-500/30 transition-all cursor-pointer shrink-0"
              >
                Filtrer uniquement Finances →
              </button>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-5">
              {financeMediums.slice(0, 8).map(medium => (
                <MediumCard
                  key={medium.id}
                  medium={medium}
                  onChatStart={onChatStart}
                  onProfileClick={onProfileClick}
                />
              ))}
            </div>

            {financeMediums.length > 8 && (
              <div className="text-center pt-2">
                <button
                  onClick={() => setActiveCategory('Finance')}
                  className="text-xs font-bold text-amber-300 hover:text-amber-100 bg-amber-950/40 hover:bg-amber-900/60 border border-amber-500/30 px-5 py-2.5 rounded-2xl transition-all cursor-pointer"
                >
                  Voir tous les {financeMediums.length} experts en Argent & Finances →
                </button>
              </div>
            )}
          </section>

        </div>
      ) : (
        /* VUE FILTRÉE PAR CATÉGORIE SPÉCIFIQUE OU RECHERCHE */
        <div>
          {/* Banner Spécifique à la catégorie active */}
          {activeCategory === 'vip' && (
            <div className="bg-gradient-to-r from-amber-950 via-[#261904] to-purple-950 border-2 border-amber-400/80 rounded-3xl p-5 sm:p-6 mb-6 shadow-[0_0_35px_rgba(251,191,36,0.3)] flex flex-col sm:flex-row items-center justify-between gap-4">
              <div className="flex items-center gap-4 text-center sm:text-left">
                <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-amber-400 to-yellow-600 flex items-center justify-center shadow-[0_0_20px_rgba(251,191,36,0.6)] animate-pulse shrink-0">
                  <CrownIcon className="w-8 h-8 text-black fill-black" />
                </div>
                <div>
                  <div className="flex items-center gap-2 justify-center sm:justify-start">
                    <h2 className="text-xl sm:text-2xl font-serif font-bold text-white">
                      Salon Privilège VIP (+1 000 Astres)
                    </h2>
                    <span className="bg-amber-400 text-black px-2 py-0.5 rounded-full text-[10px] font-black uppercase">
                      Exclusif
                    </span>
                  </div>
                  <p className="text-xs sm:text-sm text-purple-200/90 mt-1 max-w-2xl">
                    L'élite de la voyance française : nos praticiens les plus réputés, réservés exclusivement aux consultants possédant au moins 1 000 Astres sur leur compte.
                  </p>
                </div>
              </div>
              <button
                onClick={() => setActiveCategory('all')}
                className="text-xs font-semibold text-purple-300 hover:text-white bg-white/5 hover:bg-white/10 px-3.5 py-2 rounded-xl border border-white/10 transition-colors shrink-0 cursor-pointer"
              >
                ← Tous les domaines
              </button>
            </div>
          )}

          {activeCategory === 'Amour' && (
            <div className="bg-gradient-to-r from-pink-950/90 via-purple-950 to-[#120822] border border-pink-500/60 rounded-3xl p-5 mb-6 shadow-[0_0_25px_rgba(244,63,94,0.25)] flex items-center justify-between">
              <div className="flex items-center gap-3">
                <HeartIcon className="w-7 h-7 text-pink-400 fill-pink-400/40" />
                <div>
                  <h2 className="text-xl font-bold text-white">Domaine : Amour & Sentiments</h2>
                  <p className="text-xs text-pink-200/80">{filteredMediums.length} médiums spécialisés en cœur et relations</p>
                </div>
              </div>
              <button
                onClick={() => setActiveCategory('all')}
                className="text-xs font-semibold text-pink-200 hover:text-white bg-pink-950/60 px-3 py-1.5 rounded-xl border border-pink-500/30 cursor-pointer"
              >
                ← Tous les domaines
              </button>
            </div>
          )}

          {activeCategory === 'Travail' && (
            <div className="bg-gradient-to-r from-cyan-950/90 via-blue-950 to-[#081326] border border-cyan-500/60 rounded-3xl p-5 mb-6 shadow-[0_0_25px_rgba(6,182,212,0.25)] flex items-center justify-between">
              <div className="flex items-center gap-3">
                <BriefcaseIcon className="w-7 h-7 text-cyan-400" />
                <div>
                  <h2 className="text-xl font-bold text-white">Domaine : Travail & Carrière</h2>
                  <p className="text-xs text-cyan-200/80">{filteredMediums.length} médiums spécialisés en projets et carrière</p>
                </div>
              </div>
              <button
                onClick={() => setActiveCategory('all')}
                className="text-xs font-semibold text-cyan-200 hover:text-white bg-cyan-950/60 px-3 py-1.5 rounded-xl border border-cyan-500/30 cursor-pointer"
              >
                ← Tous les domaines
              </button>
            </div>
          )}

          {activeCategory === 'Finance' && (
            <div className="bg-gradient-to-r from-amber-950/90 via-yellow-950 to-[#1d1607] border border-amber-500/60 rounded-3xl p-5 mb-6 shadow-[0_0_25px_rgba(245,158,11,0.25)] flex items-center justify-between">
              <div className="flex items-center gap-3">
                <TrendingUpIcon className="w-7 h-7 text-amber-400" />
                <div>
                  <h2 className="text-xl font-bold text-white">Domaine : Argent & Finances</h2>
                  <p className="text-xs text-amber-200/80">{filteredMediums.length} médiums spécialisés en abondance et patrimoine</p>
                </div>
              </div>
              <button
                onClick={() => setActiveCategory('all')}
                className="text-xs font-semibold text-amber-200 hover:text-white bg-amber-950/60 px-3 py-1.5 rounded-xl border border-amber-500/30 cursor-pointer"
              >
                ← Tous les domaines
              </button>
            </div>
          )}

          {searchTerm && (
            <div className="mb-4 text-xs text-purple-300 font-medium">
              Résultats pour « <strong className="text-white">{searchTerm}</strong> » : {filteredMediums.length} médiums trouvés
            </div>
          )}

          {filteredMediums.length === 0 ? (
            <div className="text-center py-16 text-purple-300/70 space-y-3 bg-[#12102A]/40 rounded-3xl border border-purple-900/30 p-8">
              <SparklesIcon className="w-10 h-10 text-purple-400 mx-auto" />
              <p className="text-sm font-semibold">Aucun médium ne correspond à ce critère pour le moment.</p>
              <button
                onClick={() => { setActiveCategory('all'); setSearchTerm(''); }}
                className="text-xs bg-purple-900/80 text-purple-200 px-4 py-2 rounded-xl hover:bg-purple-800 transition-colors cursor-pointer"
              >
                Afficher tous les médiums
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-5">
              {filteredMediums.map(medium => (
                <MediumCard
                  key={medium.id}
                  medium={medium}
                  onChatStart={onChatStart}
                  onProfileClick={onProfileClick}
                />
              ))}
            </div>
          )}
        </div>
      )}

    </div>
  );
};

export default MediumsList;
